import {
  GET_ACCOUNT_CONFIG_SUCCESS,
  GET_ACCOUNT_FAILED,
  GET_ACCOUNT_REQUEST,
  GET_ACCOUNT_SUCCESS,
  GET_BILLING_PLAN_FAILED,
  GET_BILLING_PLAN_REQUEST,
  GET_BILLING_PLAN_SUCCESS,
  GET_INVOICES_SUCCESS,
  LOGOUT,
  OAUTH_SUCCESS,
  REQUEST_TOKEN_SUCCESS,
  UPDATE_ACCOUNT_VISIBILITY_SUCCESS,
  UPDATE_IMPORT_RECURRING_MEETINGS_SUCCESS,
  UPDATE_RELATIONSHIP_SCORE_SUCCESS,
} from 'actions';
import { AccountVisibilityFormValues } from 'component/form/Settings/AccountVisibilityForm';
import { CommsPreference, EntropyPeriod } from 'enums';
import { AccountConfig, BillingPlan } from 'graph/types';
import update from 'immutability-helper';
import { IInvoice, IStoreCustomField } from 'interfaces';

import { User } from './auth';
import createReducer from './createReducer';

interface ScoringModelPreference {
  entropyPeriod: EntropyPeriod;
  commsPreference: CommsPreference;
}

export interface IBillingPlan extends BillingPlan {
  hasExpired: boolean;
  isTrial: boolean;
}

interface Account {
  _id: string;
  id: string;
  email: string;
  timestamp: string;
  commitStamp: string;
  _hash: string;
  portfolios: string[];
  customFields: IStoreCustomField[];
  billing: IBillingPlan;
  billingLoading: boolean;
  invoices: IInvoice[];
  scoringModelPreference: ScoringModelPreference;
  name: string;
  settings: {
    disableDataExport: boolean;
    importRecurringMeetings?: boolean;
    hideInteractionDescriptions: boolean;
    restrictFinancialViews: boolean;
  };
  config: AccountConfig;
  features: string[];
}

interface AccountState {
  account: null | Account;
  loading: boolean;
  health: null;
}

const INITIAL_STATE: AccountState = {
  account: null,
  health: null,
  loading: false,
};

type DataAction = {
  type: string;
  data?: Account;
};

type AccountConfigAction = {
  type: string;
  data?: AccountConfig;
};

type RecurringAction = {
  type: string;
  data?: {
    importRecurringMeetings: boolean;
  };
};

type ScoringAction = {
  type: string;
  data?: ScoringModelPreference;
};

type BillingAction = {
  type: string;
  data?: IBillingPlan;
};

type InvoicesAction = {
  type: string;
  data?: {
    invoices: IInvoice[];
  };
};

type PayloadUserAction = {
  type: string;
  payload?: {
    user: User;
  };
};

type UserAction = {
  type: string;
  user?: User;
};

type VisibilitySettingsAction = {
  type: string;
  data?: AccountVisibilityFormValues;
};

export default createReducer(INITIAL_STATE, {
  [LOGOUT]: () => ({ ...INITIAL_STATE }),
  [GET_ACCOUNT_REQUEST]: (state) => {
    return { ...state, loading: true };
  },
  [GET_ACCOUNT_SUCCESS]: (state, action?: DataAction) => {
    const account = action?.data;
    if (account === undefined) {
      return state;
    }
    return { ...state, account, loading: false };
  },
  [GET_ACCOUNT_FAILED]: (state) => {
    return { ...state, loading: false };
  },
  [GET_ACCOUNT_CONFIG_SUCCESS]: (state, action?: AccountConfigAction) => {
    const config = action?.data;
    if (config === undefined) {
      return state;
    }
    return update(state, {
      account: {
        $merge: {
          config,
        },
      },
    });
  },
  [UPDATE_IMPORT_RECURRING_MEETINGS_SUCCESS]: (
    state,
    action?: RecurringAction,
  ) => {
    const data = action?.data;
    if (data === undefined) {
      return state;
    }
    return update(state, {
      account: {
        settings: {
          $merge: {
            importRecurringMeetings: data.importRecurringMeetings,
          },
        },
      },
    });
  },
  [UPDATE_ACCOUNT_VISIBILITY_SUCCESS]: (
    state,
    action?: VisibilitySettingsAction,
  ) => {
    const data = action?.data;
    if (data === undefined) {
      return state;
    }
    return update(state, {
      account: {
        settings: {
          $merge: {
            hideInteractionDescriptions: data.hideInteractionDescriptions,
            restrictFinancialViews: data.restrictFinancialViews,
          },
        },
      },
    });
  },
  [UPDATE_RELATIONSHIP_SCORE_SUCCESS]: (state, action?: ScoringAction) => {
    const data = action?.data;
    if (data === undefined) {
      return state;
    }
    return update(state, {
      account: {
        scoringModelPreference: { $set: data },
      },
    });
  },
  [GET_BILLING_PLAN_REQUEST]: (state) => {
    const updatedState = update(state, {
      account: {
        $merge: {
          billingLoading: true,
        },
      },
    });
    return updatedState;
  },
  [GET_BILLING_PLAN_SUCCESS]: (state, action?: BillingAction) => {
    const data = action?.data;
    if (data === undefined) {
      return state;
    }

    const updatedData = update(state, {
      account: {
        $merge: {
          billing: data,
          billingLoading: false,
        },
      },
    });
    return updatedData;
  },
  [GET_BILLING_PLAN_FAILED]: (state) => {
    const updatedState = update(state, {
      account: {
        $merge: {
          billingLoading: true,
        },
      },
    });
    return updatedState;
  },
  [GET_INVOICES_SUCCESS]: (state, action?: InvoicesAction) => {
    const data = action?.data;
    if (data === undefined) {
      return state;
    }
    return update(state, {
      account: {
        $merge: {
          invoices: data.invoices,
        },
      },
    });
  },
  [REQUEST_TOKEN_SUCCESS]: (state, action?: PayloadUserAction) => {
    const payload = action?.payload;
    const user = payload?.user || ({} as User);
    if (!payload) {
      return state;
    }
    return update(state, {
      account: {
        // @ts-ignore needs to be $set otherwise error - but making other properties optional will cascade. TODO
        $set: {
          id: user.accountId,
        },
      },
    });
  },
  [OAUTH_SUCCESS]: (state, action?: UserAction) => {
    const user = action?.user;
    if (!user) {
      return state;
    }
    return update(state, {
      account: {
        // @ts-ignore needs to be $set otherwise error - but making other properties optional will cascade. TODO
        $set: {
          id: user.accountId,
        },
      },
    });
  },
});
